// A machine's "Jobs at once" cap (#6740): the write state the details panel's row and the
// card menu's picker share (`components/MachineJobsAtOnce.tsx`), over
// `PUT /v1/runners/:id/max-slots` (`setBoxMaxSlots`, `./box-lifecycle`).
//
// ONE PUT PER CHOICE, no debounce: a choice, not a toggle, and the server holds the range.
// A choice while one is in flight is dropped, so one pick is one request.
//
// THE VALUE SHOWN IS THE STORED ONE. Nothing here holds a number: the control reads
// `box.max_slots` off `GET /v1/fleet/utilization`, and every write — landed or refused —
// invalidates that read, so the row and the menu show what the server stored. A refusal
// therefore needs no revert: the stored value never moved. The refusal shown is the
// API's own `detail` (`apps/dashboard/CLAUDE.md` § Boundary).
//
// Who may write is `canSetJobsAtOnce`: an admin or owner (`canAdminWrite`, owner ruling
// G21) on this org's own machine (G23: another org's machine is read-only for everyone).

import { RUNNER_MAX_SLOTS_MAX, RUNNER_MAX_SLOTS_MIN } from '@developerz/domain';
import { useQueryClient } from '@tanstack/solid-query';
import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { setBoxMaxSlots } from './box-lifecycle';
import { shortId } from './fleet-machines';
import { FLEET_UTILIZATION_QUERY_KEY, type UtilBox } from './fleetUtilization';
import { SESSION_TOKEN } from './session';

/** The choices, `RUNNER_MAX_SLOTS_MIN`..`RUNNER_MAX_SLOTS_MAX` (1..8), the domain's bounds. */
export const JOBS_AT_ONCE_CHOICES: readonly number[] = Array.from(
  { length: RUNNER_MAX_SLOTS_MAX - RUNNER_MAX_SLOTS_MIN + 1 },
  (_, i) => RUNNER_MAX_SLOTS_MIN + i,
);

/** Whether this viewer may change this machine's cap: an admin, on this org's own machine. */
export function canSetJobsAtOnce(box: UtilBox, canWrite: boolean): boolean {
  return canWrite && box.via_org === null;
}

/** The machine as the picker names it: display name plus the 8-character id, never the hostname. */
export function jobsAtOnceMachine(box: UtilBox): string {
  return `${box.display_name} ${shortId(box.id)}`;
}

export type JobsAtOnceWrite = {
  readonly pending: () => boolean;
  /** "Could not save Jobs at once. <the API's detail>", or null. */
  readonly error: () => string | null;
  /** Send one choice. Dropped while a save is in flight. */
  readonly save: (maxSlots: number) => void;
};

/**
 * The write for one machine. Must run under the app's `QueryClientProvider`: the re-read
 * is an invalidation of the utilization query, the read both mounts show.
 */
export function createJobsAtOnceWrite(input: {
  readonly box: () => UtilBox;
  readonly token?: string | null | undefined;
  /** The save landed (the card menu closes on it). */
  readonly onSaved?: () => void;
}): JobsAtOnceWrite {
  const client = useQueryClient();
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  return {
    pending,
    error,
    save: (maxSlots) => {
      if (pending()) return;
      setPending(true);
      setError(null);
      setBoxMaxSlots(input.box().id, maxSlots, input.token ?? SESSION_TOKEN)
        .then(() => input.onSaved?.())
        .catch((e: unknown) =>
          setError(t('fleet.jobs_at_once.failed', { detail: errorReason(e, t('errors.generic')) })),
        )
        .finally(() => {
          setPending(false);
          void client.invalidateQueries({ queryKey: [FLEET_UTILIZATION_QUERY_KEY] });
        });
    },
  };
}
